import { useFormik } from 'formik'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import { Button, Checkbox, CogfyLogo, Input } from '../../components'
import { useCogfy, useTitle, useToasts } from '../../hooks'

export function SignUpPage () {
  const cogfy = useCogfy()
  const { t } = useTranslation('en', { lng: 'en' })
  const toasts = useToasts()
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      acceptTerms: false,
      acceptPrivacy: false
    },
    validationSchema: yup.object().shape({
      name: yup.string().required(t('Required field')),
      email: yup.string().email(t('Invalid email')).required(t('Required field')),
      acceptTerms: yup.bool(),
      acceptPrivacy: yup.bool()
    }),

    onSubmit: async (values) => {
      try {
        const result = await cogfy.signUp({ name: values.name, email: values.email })

        navigate('/verify-email', { state: { token: result.token } })
      } catch (error) {
        if (cogfy.isConflict(error)) {
          toasts.error(t('Email already in use'))
        } else {
          toasts.error('Unexpected error')
        }
      }
    }
  })

  useTitle({
    title: t('signUpPage:Sign up')
  })

  return (
    <div className="w-full flex flex-col items-center">
      <div className="navbar max-w-screen-xl">
        <CogfyLogo size={48} />
      </div>

      <div className="w-96 p-6 mt-[8vh] mb-[16vh]">
        <div className="w-96 p-6 mt-[8vh] mb-[16vh]">
          <form onSubmit={formik.handleSubmit}>
            <h1 className="text-2xl font-semibold mb-2">
              {t('SignUp:Doing more with less')}
            </h1>
            <p className="text-xl font-semibold text-gray-400 mb-8">
              <Trans
                i18nKey={t('SignUp:Create your Cogfy account')}
                components={{
                  cogfy: (
                    <span className="bg-gradient-to-r from-[#FF85E3] to-[#FBC97F] text-transparent bg-clip-text" />
                  )
                }}
              />
            </p>
            <Input
              disabled={formik.isSubmitting}
              label={t('Name')}
              error={Boolean(formik.touched.name && formik.errors.name)}
              helperText={(formik.touched.name && formik.errors.name) ?? ''}
              {...formik.getFieldProps('name')}
            />
            <Input
              disabled={formik.isSubmitting}
              label="Email"
              error={Boolean(formik.touched.email && formik.errors.email)}
              helperText={(formik.touched.email && formik.errors.email) ?? ''}
              {...formik.getFieldProps('email')}
            />
            <div className='flex flex-col text-xs mt-4 gap-1'>
              <label className='flex gap-1'>
                <Checkbox
                  size='xs'
                  {...formik.getFieldProps('acceptTerms')}
                  checked={formik.values.acceptTerms}
                />
                {t('signUpPage:I agree to the')}
                <a
                  href="https://cogfy.com/terms"
                  className='link'
                  target='_blank'
                  rel='noreferrer'
                >
                  {t('signUpPage:Terms of Service')}
                </a>
              </label>
              {formik.touched.acceptTerms && formik.errors.acceptTerms && (
                <span className="text-red-500">{formik.errors.acceptTerms}</span>
              )}

              <label className='flex gap-1'>
                <Checkbox
                  size='xs'
                  {...formik.getFieldProps('acceptPrivacy')}
                  checked={formik.values.acceptPrivacy}
                />
                {t('signUpPage:I agree to the')}
                <a
                  href="https://cogfy.com/privacy-policy"
                  className='link'
                  target='_blank'
                  rel='noreferrer'
                >
                  {t('signUpPage:Privacy Policy')}
                </a>
              </label>
            </div>
            <Button
              type="submit"
              color="primary"
              className="w-full mt-4"
              disabled={formik.isSubmitting || !formik.values.acceptTerms || !formik.values.acceptPrivacy}
            >
              {t('signUpPage:Sign up')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}
